/* ============================================================
   SHMEAT — MOTION ENGINE
   Lenis smooth-scroll + GSAP choreography + the seasoning
   sprinkle particle system. Self-contained, no build step.
   Tasteful by default: honours prefers-reduced-motion and
   never blocks content if a library fails to load.
   ============================================================ */
(function () {
  'use strict';

  var REDUCE = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var TOUCH = window.matchMedia('(pointer: coarse)').matches;
  var DESIGN = !!(window.Shopify && window.Shopify.designMode);
  var hasGSAP = !!window.gsap;
  var hasST = !!window.ScrollTrigger;
  var lenis = null;

  if (hasGSAP && hasST) gsap.registerPlugin(ScrollTrigger);

  function debounce(fn, wait) {
    var t;
    return function () {
      var ctx = this, args = arguments;
      clearTimeout(t);
      t = setTimeout(function () { fn.apply(ctx, args); }, wait);
    };
  }
  function rand(a, b) { return a + Math.random() * (b - a); }

  /* --- Failsafe: never leave hero content hidden ------------- */
  function reveal() { document.documentElement.classList.add('shmeat-revealed'); }
  setTimeout(reveal, 800);

  /* --- 1. Smooth scroll (Lenis) ------------------------------ */
  function initLenis() {
    if (REDUCE || TOUCH || DESIGN || !window.Lenis) return;
    // lerp (per-frame catch-up) feels far more connected than a long
    // duration glide — 0.1 keeps it smooth without the laggy after-slide.
    lenis = new Lenis({
      lerp: 0.1,
      smoothWheel: true,
      wheelMultiplier: 1,
      touchMultiplier: 1.6
    });
    if (hasGSAP && hasST) {
      lenis.on('scroll', ScrollTrigger.update);
      gsap.ticker.add(function (time) { lenis.raf(time * 1000); });
      gsap.ticker.lagSmoothing(0);
    } else {
      var raf = function (time) { lenis.raf(time); requestAnimationFrame(raf); };
      requestAnimationFrame(raf);
    }
  }


  /* --- 1b. In-page anchor links ------------------------------
     Lenis owns the scroll position, so a native #hash jump fights it
     (and on touch/reduced-motion Lenis is off entirely). Handle both:
     hand the target to Lenis when it's running, otherwise fall back to
     native smooth scrolling. Offset for the sticky header either way. */
  function headerOffset() {
    var h = document.querySelector('.header-wrapper, .section-header, header');
    if (!h) return 0;
    var pos = window.getComputedStyle(h).position;
    return (pos === 'sticky' || pos === 'fixed') ? h.getBoundingClientRect().height : 0;
  }

  function initAnchors() {
    document.addEventListener('click', function (e) {
      var link = e.target.closest('a[href*="#"]');
      if (!link) return;
      var url = new URL(link.href, window.location.href);
      if (url.pathname !== window.location.pathname || url.host !== window.location.host) return;
      var id = url.hash.slice(1);
      if (!id) return;
      var target = document.getElementById(id);
      if (!target) return;

      e.preventDefault();
      var offset = -headerOffset() - 8;

      if (lenis) {
        lenis.scrollTo(target, { offset: offset, duration: 1.1 });
      } else {
        var top = target.getBoundingClientRect().top + window.pageYOffset + offset;
        window.scrollTo({ top: top, behavior: REDUCE ? 'auto' : 'smooth' });
      }
      history.pushState(null, '', url.hash);
    });
  }

  /* --- 1c. Pause smooth scroll while the cart drawer is open ---
     Lenis swallows wheel events, so without this the drawer (and the
     item list inside it) can't be scrolled and the checkout button
     sits unreachable below the fold on shorter viewports. The drawer
     itself carries data-lenis-prevent so native scrolling works inside
     it; stopping Lenis keeps the page behind from moving too. */
  function initDrawerPause() {
    var drawer = document.querySelector('cart-drawer');
    if (!drawer || !('MutationObserver' in window)) return;
    var wasOpen = false;
    function sync() {
      var open = drawer.classList.contains('active');
      if (open === wasOpen || !lenis) return;
      wasOpen = open;
      open ? lenis.stop() : lenis.start();
    }
    new MutationObserver(sync).observe(drawer, { attributes: true, attributeFilter: ['class'] });
    sync();
  }

  /* --- 2. Seasoning sprinkle (canvas particles) -------------- */
  // Warm seasoning tones, weighted toward tan/cream with the
  // occasional burgundy ember. Particles fall like rub off a
  // shaker; scrolling intensifies the sprinkle.
  var PALETTE = ['#E0A52E', '#E7D8B0', '#F3EFE1', '#C9A86A', '#D8BE86', '#B8893F'];

  function initSprinkle(canvas) {
    if (!canvas || canvas.dataset.sprinkleReady === '1') return;
    canvas.dataset.sprinkleReady = '1';

    var ctx = canvas.getContext('2d');
    var host = canvas.parentElement;
    var DPR = Math.min(window.devicePixelRatio || 1, 1.5);
    var intensity = parseFloat(canvas.dataset.intensity) || 1;
    var w = 0, h = 0, particles = [], rafId = null, running = false;
    var last = 0, boost = 0;

    function makeP(fromTop) {
      return {
        x: rand(0, w),
        y: fromTop ? rand(-h * 0.25, -4) : rand(0, h),
        vy: rand(18, 58),
        vx: rand(-7, 7),
        size: rand(0.6, 2.4),
        rot: rand(0, Math.PI),
        color: PALETTE[(Math.random() * PALETTE.length) | 0],
        alpha: rand(0.1, 0.6),
        sway: rand(0.4, 1.5),
        phase: rand(0, Math.PI * 2)
      };
    }

    function seed() {
      var area = w * h;
      var count = Math.round(Math.max(24, Math.min(80, area / 14000)) * intensity);
      if (REDUCE) count = Math.round(count * 0.45);
      particles = [];
      for (var i = 0; i < count; i++) particles.push(makeP(false));
    }

    function resize() {
      var r = host.getBoundingClientRect();
      w = Math.max(1, r.width); h = Math.max(1, r.height);
      canvas.width = w * DPR; canvas.height = h * DPR;
      canvas.style.width = w + 'px'; canvas.style.height = h + 'px';
      ctx.setTransform(DPR, 0, 0, DPR, 0, 0);
      seed();
    }

    function draw(ts) {
      var dt = last ? Math.min(0.05, (ts - last) / 1000) : 0.016;
      last = ts;
      boost *= 0.93;
      var mult = 1 + boost;
      ctx.clearRect(0, 0, w, h);
      for (var i = 0; i < particles.length; i++) {
        var p = particles[i];
        p.y += p.vy * mult * dt;
        p.x += (p.vx + Math.sin(ts * 0.001 * p.sway + p.phase) * 9) * dt;
        if (p.y > h + 6) { particles[i] = makeP(true); continue; }
        ctx.globalAlpha = p.alpha;
        ctx.fillStyle = p.color;
        ctx.beginPath();
        ctx.ellipse(p.x, p.y, p.size, p.size * 0.78, p.rot, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.globalAlpha = 1;
      if (running && !REDUCE) rafId = requestAnimationFrame(draw);
    }

    function start() { if (running || REDUCE) return; running = true; last = 0; rafId = requestAnimationFrame(draw); }
    function stop() { running = false; if (rafId) cancelAnimationFrame(rafId); rafId = null; }

    function kick(v) { boost = Math.min(boost + v, 3.2); }
    window.addEventListener('wheel', function () { kick(0.5); }, { passive: true });
    if (lenis) lenis.on('scroll', function (e) { kick(Math.min(Math.abs(e.velocity || 0) * 0.04, 1.2)); });

    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (entries) {
        entries.forEach(function (en) { en.isIntersecting ? start() : stop(); });
      }, { threshold: 0 }).observe(host);
    }

    window.addEventListener('resize', debounce(resize, 200));
    resize();
    if (REDUCE) { running = true; draw(16); running = false; } else start();
  }

  /* --- 3. Hero entrance + parallax --------------------------- */
  function initHero(hero) {
    if (!hero) return;
    var lines = hero.querySelectorAll('[data-hero-line] > span');
    var fades = hero.querySelectorAll('[data-hero-fade]');
    var script = hero.querySelector('[data-hero-script]');
    var stamp = hero.querySelector('[data-hero-stamp]');

    // If the parse-time failsafe already revealed the hero, the scripts
    // arrived too late to animate — showing it then yanking it back out of
    // view to replay the intro is worse than no intro at all.
    if (!hasGSAP || REDUCE || window.__shmeatRevealedEarly) { reveal(); return; }
    clearTimeout(window.__shmeatFailsafe);

    gsap.set(lines, { yPercent: 115 });
    gsap.set(fades, { opacity: 0, y: 28 });
    if (script) gsap.set(script, { clipPath: 'inset(0 100% 0 0)' });
    if (stamp) gsap.set(stamp, { scale: 1.7, rotation: -26, opacity: 0, transformOrigin: '50% 50%' });
    document.documentElement.classList.remove('shmeat-revealed');

    var tl = gsap.timeline({ defaults: { ease: 'power3.out' }, onComplete: reveal });
    tl.to(fades, { opacity: 1, y: 0, duration: 0.85, stagger: 0.12 }, 0.1)
      .to(lines, { yPercent: 0, duration: 1.1, stagger: 0.12 }, 0.4);
    if (script) {
      tl.to(script, { clipPath: 'inset(0 0% 0 0)', duration: 0.95, ease: 'power2.out' }, 0.9);
    }
    if (stamp) {
      tl.to(stamp, { opacity: 1, duration: 0.1 }, 1.05)
        .to(stamp, { scale: 1, rotation: -8, duration: 0.55, ease: 'back.out(2.4)' }, '<')
        .to(stamp, { keyframes: [{ scale: 1.05, duration: 0.07 }, { scale: 1, duration: 0.13 }] }, '>-0.05');
    }

    if (hasST) {
      var content = hero.querySelector('[data-hero-content]');
      if (content) {
        gsap.to(content, {
          yPercent: -14, opacity: 0.25, ease: 'none',
          scrollTrigger: { trigger: hero, start: 'top top', end: 'bottom top', scrub: true }
        });
      }
      var wm = hero.querySelector('.shmeat-hero__watermark');
      if (wm) {
        gsap.to(wm, {
          yPercent: 10, ease: 'none',
          scrollTrigger: { trigger: hero, start: 'top top', end: 'bottom top', scrub: true }
        });
      }
    }
  }

  /* --- 4. Marquee velocity skew ------------------------------ */
  function initMarquee() {
    if (!hasGSAP || !hasST || REDUCE) return;
    document.querySelectorAll('[data-marquee-track]').forEach(function (track) {
      var setSkew = gsap.quickTo(track, 'skewX', { duration: 0.5, ease: 'power3' });
      ScrollTrigger.create({
        onUpdate: function (self) {
          setSkew(gsap.utils.clamp(-7, 7, self.getVelocity() / -320));
        }
      });
    });
  }

  /* --- Boot -------------------------------------------------- */
  // Generic tasteful fade-up reveal for any [data-shmeat-reveal] element
  function initReveals() {
    if (!hasGSAP || !hasST || REDUCE) return;
    var els = document.querySelectorAll('[data-shmeat-reveal]');
    if (!els.length) return;
    gsap.set(els, { opacity: 0, y: 42 });
    ScrollTrigger.batch(els, {
      start: 'top 86%',
      onEnter: function (batch) {
        gsap.to(batch, { opacity: 1, y: 0, duration: 0.9, stagger: 0.12, ease: 'power3.out' });
      }
    });
  }

  function boot() {
    initLenis();
    initAnchors();
    initDrawerPause();
    document.querySelectorAll('[data-sprinkle]').forEach(initSprinkle);
    document.querySelectorAll('[data-shmeat-hero]').forEach(initHero);
    initMarquee();
    initReveals();
  }

  if (document.readyState !== 'loading') boot();
  else document.addEventListener('DOMContentLoaded', boot);

  // Theme-editor support: re-init when a section is re-rendered.
  document.addEventListener('shopify:section:load', function (e) {
    e.target.querySelectorAll('[data-sprinkle]').forEach(initSprinkle);
    e.target.querySelectorAll('[data-shmeat-hero]').forEach(initHero);
  });
})();
